import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets } from 'lucide-react';

const t = {
  tag: 'PAANI TEST',
  tagline: 'Paani rok sake, toh hi asli cement hai.',
  standard: 'Standard Cement Wall',
  lion: 'Lion Cement Wall',
  cta1: 'Free Sample Mangayein',
  cta2: 'Khud Test Karein',
  disclaimer: 'Result anumaan hain aur usage, conditions aur location ke anusar alag ho sakte hain.',
};

const WaterTestWall = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setIsPlaying(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-surface relative overflow-hidden" data-testid="water-test-section">
      <div className="absolute inset-0 bg-grid opacity-10" />

      <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10">
        {/* Header */}
        <div className="mb-10">
          <span className="text-blue-400 font-mono text-sm mb-3 block flex items-center gap-2">
            <Droplets className="w-4 h-4" /> {t.tag}
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {t.tagline}
          </h2>
        </div>

        {/* Side by side walls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Standard Wall */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-void" data-testid="water-standard-wall">
            <div className="p-4 text-center border-b border-white/5">
              <span className="text-gray-400 text-sm font-display font-medium">{t.standard}</span>
            </div>
            <div className="relative h-64 md:h-80 overflow-hidden">
              <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(0deg, #2a2520 0px, #2a2520 30px, #1a1815 30px, #1a1815 32px), repeating-linear-gradient(90deg, #2a2520 0px, #2a2520 60px, #1a1815 60px, #1a1815 62px)' }} />
              {isPlaying && (
                <>
                  <motion.div className="absolute top-0 left-1/2 -translate-x-1/2 w-16"
                    initial={{ height: 0 }} animate={{ height: '100%' }}
                    transition={{ duration: 2, ease: 'easeIn' }}>
                    <div className="w-full h-full bg-gradient-to-b from-blue-400/80 via-blue-500/60 to-blue-600/40 rounded-b-full" />
                  </motion.div>
                  <motion.div className="absolute bottom-0 left-0 right-0"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: '60%', opacity: 0.7 }}
                    transition={{ duration: 3, delay: 1.5, ease: 'easeOut' }}>
                    <div className="w-full h-full" style={{ background: 'linear-gradient(to top, rgba(59,130,246,0.5) 0%, rgba(59,130,246,0.3) 40%, rgba(59,130,246,0.1) 70%, transparent 100%)' }} />
                  </motion.div>
                  {[...Array(6)].map((_, i) => (
                    <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-blue-400/60"
                      style={{ left: `${20 + i * 12}%` }}
                      initial={{ top: '30%', opacity: 0 }}
                      animate={{ top: '95%', opacity: [0, 0.8, 0] }}
                      transition={{ duration: 2, delay: 2 + i * 0.4, repeat: Infinity, repeatDelay: 1.5 }} />
                  ))}
                  <motion.div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500/80 text-white text-xs font-display font-bold px-3 py-1 rounded-full"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 3 }}>
                    SEEPAGE
                  </motion.div>
                </>
              )}
            </div>
          </div>

          {/* Lion Wall */}
          <div className="relative rounded-2xl overflow-hidden border border-coral/20 bg-void" data-testid="water-lion-wall">
            <div className="p-4 text-center border-b border-coral/10 bg-coral/5">
              <span className="text-coral text-sm font-display font-semibold">{t.lion}</span>
            </div>
            <div className="relative h-64 md:h-80 overflow-hidden">
              <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(0deg, #2e2820 0px, #2e2820 30px, #1e1a15 30px, #1e1a15 32px), repeating-linear-gradient(90deg, #2e2820 0px, #2e2820 60px, #1e1a15 60px, #1e1a15 62px)' }} />
              {isPlaying && (
                <>
                  <motion.div className="absolute top-0 left-1/2 -translate-x-1/2 w-16"
                    initial={{ height: 0 }} animate={{ height: '100%' }}
                    transition={{ duration: 2, ease: 'easeIn' }}>
                    <div className="w-full h-full bg-gradient-to-b from-blue-400/80 via-blue-500/40 to-transparent rounded-b-full" />
                  </motion.div>
                  <motion.div className="absolute bottom-0 left-0 right-0"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: '8%', opacity: 0.4 }}
                    transition={{ duration: 3, delay: 1.5, ease: 'easeOut' }}>
                    <div className="w-full h-full bg-gradient-to-t from-blue-400/30 to-transparent" />
                  </motion.div>
                  <motion.div className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.15, 0.05] }}
                    transition={{ duration: 2, delay: 1.5 }}
                    style={{ background: 'radial-gradient(circle at center, rgba(232,90,28,0.3) 0%, transparent 70%)' }} />
                  <motion.div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-mint/80 text-void text-xs font-display font-bold px-3 py-1 rounded-full"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 3 }}>
                    PROTECTED
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" data-testid="water-cta-sample"
            className="flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-8 py-4 rounded-full transition-all">
            {t.cta1} <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/strength-scanner" data-testid="water-cta-test"
            className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-8 py-4 rounded-full transition-all">
            {t.cta2}
          </Link>
        </div>

        <p className="text-gray-600 text-xs text-center mt-6">{t.disclaimer}</p>
      </div>
    </section>
  );
};

export default WaterTestWall;
